/**
 * 過去実績「採用」の小売別取込 (需要想定 過去実績「採用」の小売別取込 Design Note
 * demand-forecast-adopt-actual-per-retailer.md Decision 2/3/6/9) の表示値選択。
 *
 * 48コマ表参照列 (`useDemandForecastGridData.pastActualGridData` →
 * `forecastSectionView.series.referenceColumns`) とチャート系列
 * (`forecastSectionView.series.pastActuals`) の両方が、`buildForecastSectionView`
 * から同じ規則で本関数を呼ぶ (Verification Strategy F1: 「配置は実装に委ねる」)。
 *
 * 入力は KomaSlots ベースに統一する (proto SlotValue[] ではない) — grid 側は
 * `useDemandForecastGridData` が変換済みの値を渡し、chart 側は
 * `buildForecastSectionView` が呼び出し前後で proto ⇔ KomaSlots を変換する
 * (`slotHelpers.protoSlotsToKoma` / `komaToProtoSlots`)。1 つの入力型に統一する
 * ことで、選択ロジック自体は proto を知らない。
 *
 * 選択キーは page-level の `selectedRetailerBusinessCd` ではなく editor の
 * 書込先 (`currentEditRow.retailerBusinessCd`) を使う — 配下 1 件の BG 全体
 * 表示では前者が undefined のままだが、後者は唯一の小売の値になっている
 * (前提6)。呼び出し元はこの値をそのまま渡すだけで良い。
 */
import type {
  KomaSlots,
  TargetScope,
} from '~/pages/DemandForecastDaily/models/types'

/** 48コマ分すべて空 (undefined) の KomaSlots。 */
function emptyKomaSlots(): KomaSlots {
  return Array.from({ length: 48 }, () => undefined)
}

/**
 * 過去実績列の採用ボタン非活性理由 (Decision 3/9)。小売編集対象でのみ発生し、
 * i18n 文言への解決は消費側 (KomaGrid.tsx) が行う (models は i18n しない)。
 */
export type PastActualColumnDisabledReasonKey =
  | 'notBrokenDown'
  | 'noRetailerActual'

/** 1 小売分の owner 軸解決済み実績値 (`retailerSlots` の KomaSlots 版要素)。 */
export type RetailerActualColumn = {
  retailerBusinessCd: string
  slots: KomaSlots
}

/** `selectPastActualColumn` の入力 (PastActual の owner 軸の系列と分解可否)。 */
export type PastActualOwnerBreakdownInput = {
  /** エリア合計 (proto `PastActual.slots` の KomaSlots 変換)。 */
  slots: KomaSlots
  unattributedSlots?: KomaSlots
  retailerSlots: RetailerActualColumn[]
  ownerBreakdownAvailable: boolean
}

export type ResolvedPastActualColumn = {
  /** 表示値 (48要素、Decision 2/6)。 */
  slots: KomaSlots
  /**
   * 採用ボタンの非活性理由 (Decision 3/9)。BG 全体 (配下複数) では分解不能
   * ('notBrokenDown') の時だけ付く (AC-DFD-047d、issue #7436: 集計表示でも
   * 各小売の帰属実績を取り込むため、分解できない日は取り込めない)。
   */
  disabledReasonKey: PastActualColumnDisabledReasonKey | undefined
}

/**
 * 過去実績 1 列 (1 日) の表示値と採用ボタン非活性理由を選択する (Decision 2/3/6/9)。
 *
 * - 帰属不明: 専用の unattributedSlots を表示し、小売やエリア合計へ代替しない。
 * - BG 全体・配下複数: エリア合計 (`slots`) を表示する。
 *   採用は各小売の帰属実績を取り込む (AC-DFD-047d) ため、分解不能
 *   (`ownerBreakdownAvailable=false`) なら 'notBrokenDown' を付ける (エリア合計
 *   を取り込む代替はしない)。小売ごとの実績の有無は採用時に呼び出し元が判定し
 *   通知する (AC-DFD-047e)。
 * - 小売編集対象かつ分解不能 (`ownerBreakdownAvailable=false`): 空表示 +
 *   'notBrokenDown'。エリア合計へフォールバックしない (Decision 3 棄却案 (b)、
 *   二重計上の再発防止が本関数最大の禁止事項)。
 * - 小売編集対象かつ分解可・小売選択済み: 該当小売の `retailerSlots` エントリを
 *   表示する (無ければ空 slot = 実績なし)。全コマ空なら 'noRetailerActual'
 *   (Decision 9)。
 * - 小売編集対象だが小売未選択 (`retailerBusinessCd` 不在): 空表示だが
 *   非活性理由は付けない — 「小売未選択」は既存の page-level 理由
 *   (`adjustmentDisabledReasonKey='noRetailer'`) が別途担当する範囲であり、
 *   Decision 9 は「選択済みの小売に実績が無い」ケースのみを対象とする
 *   (両者を混同すると、小売を選んでいないだけの操作者に「実績がありません」と
 *   誤った理由を見せてしまう)。
 */
export function selectPastActualColumn(
  pa: PastActualOwnerBreakdownInput,
  targetScope: TargetScope,
  retailerBusinessCd: string | undefined,
): ResolvedPastActualColumn {
  if (targetScope === 'UNATTRIBUTED')
    return {
      slots: pa.unattributedSlots ?? emptyKomaSlots(),
      disabledReasonKey: undefined,
    }
  if (targetScope === 'BG_TOTAL') {
    return {
      slots: pa.slots,
      disabledReasonKey: pa.ownerBreakdownAvailable
        ? undefined
        : 'notBrokenDown',
    }
  }
  if (!pa.ownerBreakdownAvailable) {
    return { slots: emptyKomaSlots(), disabledReasonKey: 'notBrokenDown' }
  }
  if (retailerBusinessCd === undefined) {
    return { slots: emptyKomaSlots(), disabledReasonKey: undefined }
  }
  const entry = pa.retailerSlots.find(
    (rs) => rs.retailerBusinessCd === retailerBusinessCd,
  )
  const slots = entry?.slots ?? emptyKomaSlots()
  const hasValue = slots.some((v) => v !== undefined)
  return {
    slots,
    disabledReasonKey: hasValue ? undefined : 'noRetailerActual',
  }
}
